<script setup>
import Timeline from "primevue/timeline";
import { computed, ref } from "vue";
import { useRoute } from "vue-router";
import useExperiences from "@/components/MyExperiences/composables/useExperiences.js";
import MobileAppBanner from "@/components/MyExperiences/DownloadMobileApp.vue";
import MyExperienceDetailOverview from "@/components/MyExperiences/MyExperienceDetail/MyExperienceDetailOverview.vue";
import MyExperienceDetailSelections from "@/components/MyExperiences/MyExperienceDetail/MyExperienceDetailSelections.vue";
import ProgramChangeInfoModal from "@/components/MyExperiences/MyExperienceDetail/ProgramChangeInfoModal.vue";
import ArrowLeft from "@/components/shared/icons/ArrowLeft.vue";
import NewTabIcon from "@/components/shared/icons/NewTabIcon.vue";

const route = useRoute();
const applicationId = computed(() => route?.params?.applicationId);
const openProgramChangeInfoModal = ref(false);
const {
  profileDevices,
  experiencesIsLoading,
  getExperienceDetails,
  programStartMessage,
  showStartMessage,
  formatPrettyDate,
} = useExperiences();
const showMobileAppBanner = computed(() => (profileDevices.value?.length ?? 0) === 0 && !experiencesIsLoading.value);
const { experienceDetail, experienceDetailIsLoading } = getExperienceDetails(applicationId.value);

const showViewApplication = computed(() => {
  return experienceDetail.value?.session_require_application && experienceDetail.value?.order_status === "Submitted";
});
</script>
<template>
  <div class="mx-auto px-4 bg-blue-100">
    <div v-if="showViewApplication" class="flex items-center flex-wrap text-sm px-2 py-4">
      <ArrowLeft custom-class="w-3 h-3" />
      <router-link
        class="text-indigo-base text-sm font-semibold ml-2"
        :to="{ name: 'applications/landing', params: { applicationId } }"
        data-test-id="application-breadcrumb"
      >
        View This Application
      </router-link>
    </div>
    <div class="flex flex-col lg:flex-row gap-6 pt-6 px-2">
      <div class="flex-1 lg:basis-3/4">
        <div class="flex items-center flex-wrap text-cyan-200 pb-6 text-2xl font-bold">
          <router-link
            :to="{
              name: 'program-detail-view',
              params: { programPageId: experienceDetail.program_page_id },
              query: { 'session-id': experienceDetail.session_id },
            }"
            target="_blank"
            rel="noopener noreferrer"
            class="flex items-center"
            data-test-id="session-name-routerlink"
          >
            <span>{{ experienceDetail.session_name }}</span>
            <new-tab-icon class="ml-3" custom-class="w-4 h-4" />
          </router-link>
        </div>
        <Timeline :value="['Placeholder', 'Application in Review', 'Accepted']" layout="horizontal">
          <template #content="slotProps">
            {{ slotProps.item }}
          </template>
        </Timeline>
        <MyExperienceDetailOverview />
      </div>
      <div class="flex-1 lg:basis-1/4 text-right text-sm">
        <p class="text-[#334155]">
          Program Dates: {{ formatPrettyDate(experienceDetail.start_date) }} -
          {{ formatPrettyDate(experienceDetail.end_date) }}
        </p>
        <p v-if="showStartMessage(experienceDetail.start_date)" class="text-[#334155]">
          {{ programStartMessage(experienceDetail.start_date) }}
        </p>
        <div v-if="experienceDetail.session_owner_is_api" class="font-semibold text-cyan-200 pt-6">
          <p
            class="cursor-pointer"
            data-test-id="program-change-info-modal"
            @click="openProgramChangeInfoModal = true"
          >
            Program Change, Deferral, or Withdrawal
          </p>
          <a
            v-if="experienceDetail.session_cancellation_policy_url"
            :href="experienceDetail.session_cancellation_policy_url"
            target="_blank"
            rel="noopener noreferrer"
            data-test-id="cancelation-policy-link"
          >
            Withdrawal Policy
          </a>
        </div>
        <div class="h-screen">
          <div class="py-4">
            <mobile-app-banner v-if="showMobileAppBanner" layout="col" />
          </div>
          <MyExperienceDetailSelections :experience-detail="experienceDetail" />
        </div>
      </div>
    </div>
  </div>
  <ProgramChangeInfoModal v-model="openProgramChangeInfoModal" />
</template>
